import { ClientRecipients, type ClientRecipient } from '@codezo/client-detail';
import { InvoiceRecipientType } from '@codezo/graphql';
import { Mail, Plus, Users, X } from 'lucide-react';
import { useState } from 'react';
import { Button, buttonVariants } from '../../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../../../components/ui/dialog';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { Field } from '../../../components/ui/field';
import { Input } from '../../../components/ui/input';
import { SelectPopover } from '../../../components/ui/select-popover';
import { useInvoiceRecipients } from './useInvoiceRecipients';

/**
 * The invoice recipients row inside the Invoicing settings group.
 *
 * Behaviour comes from `useInvoiceRecipients`; the list itself is the package's
 * `ClientRecipients`, so it sits inside the group as one more row rather than
 * arriving as a second card with its own heading — which is what v1's section
 * did, and why it looked bolted on.
 *
 * Adding splits by kind because the two are genuinely different: a system user
 * is picked from a list and stored by id (so a rename follows), while an
 * external address is typed and validated.
 */
export function InvoiceRecipientsRow({ organizationId }: { organizationId: string }) {
	const r = useInvoiceRecipients(organizationId);

	const [mode, setMode] = useState<InvoiceRecipientType | null>(null);
	const [pendingUserId, setPendingUserId] = useState('');
	const [emailInput, setEmailInput] = useState('');
	const [nameInput, setNameInput] = useState('');
	/* Removing is one click on a small × and the list is saved whole, so there is
	   nothing to undo — worth a confirm even though v1 removed outright. */
	const [removeTarget, setRemoveTarget] = useState<ClientRecipient | null>(null);

	const close = () => setMode(null);
	const emailError = r.validateEmail(emailInput);

	/* The stored shape carries a user id; the list wants a name and an email,
	   resolved live so a renamed user is not listed under their old name. */
	const rows: ClientRecipient[] = r.recipients.map((recipient) => {
		if (recipient.type === InvoiceRecipientType.User) {
			const resolved = r.userLabel(recipient.userId, recipient.name);
			return {
				id: recipient.key,
				name: resolved.name,
				email: resolved.email ?? '',
				isExternal: false,
			};
		}
		return {
			id: recipient.key,
			name: recipient.name ?? null,
			email: recipient.email ?? '',
			isExternal: true,
		};
	});

	return (
		<>
			<ClientRecipients
				recipients={rows}
				action={
					<DropdownMenu>
						{/* Styled with `buttonVariants` rather than rendered AS our
						    `Button`: every working dropdown in this app puts the
						    classes on the trigger itself, and routing it through
						    another Base UI button component silently dropped the
						    trigger's own click handling. */}
						<DropdownMenuTrigger
							disabled={r.saving}
							className={buttonVariants({ variant: 'outline', size: 'sm' })}
						>
							<Plus size={13} />
							Add recipient
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem
								onClick={() => {
									setPendingUserId('');
									setMode(InvoiceRecipientType.User);
								}}
							>
								<Users size={14} /> From contacts
							</DropdownMenuItem>
							<DropdownMenuItem
								onClick={() => {
									setEmailInput('');
									setNameInput('');
									setMode(InvoiceRecipientType.Email);
								}}
							>
								<Mail size={14} /> External email
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				}
				renderRecipientAction={(recipient) => (
					<Button
						variant="ghost"
						size="sm"
						aria-label={`Remove ${recipient.name || recipient.email}`}
						disabled={r.removingKey === recipient.id || r.saving}
						onClick={() => setRemoveTarget(recipient)}
					>
						<X size={13} />
					</Button>
				)}
			/>

			<ConfirmDialog
				open={!!removeTarget}
				onOpenChange={(v) => !v && setRemoveTarget(null)}
				title="Remove recipient"
				description={
					<>
						{removeTarget?.name || removeTarget?.email} will stop receiving this
						client&rsquo;s invoices.
						{removeTarget?.isExternal
							? ' The address is not stored anywhere else, so adding it back means typing it again.'
							: ' Their account and access are unaffected.'}
					</>
				}
				confirmLabel="Remove"
				loading={r.saving}
				onConfirm={async () => {
					if (!removeTarget) return;
					await r.remove(removeTarget.id);
					setRemoveTarget(null);
				}}
			/>

			{/* ── Add a system user ── */}
			<Dialog open={mode === InvoiceRecipientType.User} onOpenChange={(v) => !v && close()}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Add from contacts</DialogTitle>
						<DialogDescription>
							Stored by user, so their invoices follow a name or email change.
						</DialogDescription>
					</DialogHeader>
					<DialogBody>
						<Field label="Contact" required>
							{r.userOptions.length > 0 ? (
								<SelectPopover
									value={pendingUserId}
									onChange={setPendingUserId}
									options={r.userOptions}
									placeholder="Select a contact"
								/>
							) : (
								<p className="text-xs text-text-4">
									Every contact is already a recipient.
								</p>
							)}
						</Field>
					</DialogBody>
					<DialogFooter>
						<Button variant="outline" onClick={close} disabled={r.saving}>
							Cancel
						</Button>
						<Button
							disabled={!pendingUserId || r.saving}
							onClick={async () => {
								if (await r.addUser(pendingUserId)) close();
							}}
						>
							{r.saving ? 'Adding…' : 'Add recipient'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* ── Add an external address ── */}
			<Dialog open={mode === InvoiceRecipientType.Email} onOpenChange={(v) => !v && close()}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Add external email</DialogTitle>
						<DialogDescription>
							For an address with no account here — an accountant or a shared inbox.
						</DialogDescription>
					</DialogHeader>
					<DialogBody className="flex flex-col gap-4">
						<Field label="Email" required error={emailError ?? undefined}>
							<Input
								value={emailInput}
								onChange={(e) => setEmailInput(e.target.value)}
								placeholder="invoices@example.com"
								aria-invalid={!!emailError}
							/>
						</Field>
						<Field label="Name">
							<Input
								value={nameInput}
								onChange={(e) => setNameInput(e.target.value)}
								placeholder="Optional — who this address belongs to"
							/>
						</Field>
					</DialogBody>
					<DialogFooter>
						<Button variant="outline" onClick={close} disabled={r.saving}>
							Cancel
						</Button>
						<Button
							disabled={!r.canAddEmail(emailInput) || r.saving}
							onClick={async () => {
								if (await r.addEmail(emailInput, nameInput)) close();
							}}
						>
							{r.saving ? 'Adding…' : 'Add recipient'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
